import { getWidth } from "components/MainContainer";
import { MAX_GUEST_SEATS } from "constants/";
import {
  BestTicketBlockDetails,
  PlanPackageComponentDetails,
  SeatDetails,
  SectionDetails,
  SubscriptionDetails,
  TenantSubdomain,
  TicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppSelector } from "hooks/useAppSelector";
import { ScheduleStackParamList } from "navigation/types";
import {
  fetchBestBlocksRequest,
  fetchSectionsRequest,
  holdForUpdateReservationRequest,
  holdTicketsRequest,
} from "queries/ticket.queries";
import React, { useEffect, useMemo, useState } from "react";
import { View, Platform } from "react-native";
import SectionList from "screens/SeatSelection/components/SectionList";
import toast from "utils/toast";
import { PRICE_LOW_TO_HIGH, SeatSelectionState } from "./types";
import VenueSeatMap from "./components/VenueSeatMap";
import {
  filterAccessible,
  filterChangingSeatsSectionRow,
  filterPremium,
  sortBlocksFunc,
} from "screens/SeatSelection/helpers";
import InnerLoading from "components/InnerLoading";
import { useAppDispatch } from "hooks/useAppDispatch";
import {
  setActualHeldDetail,
  setIsChangingSeat,
  setOriginalEventId,
  setOriginalHeldDetail,
  setSelectedPartySize,
} from "features/slices/reservationFlowSlice";
import { useMutation } from "@tanstack/react-query";
import { useNavigation } from "@react-navigation/native";
import Filters from "screens/SeatSelection/components/Filters";
import { getRemainingSlotsForPlanPackage } from "screens/Reserve/helpers";
import i18n from "plugins/localization";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import SeatQuantitySelector from "./components/SeatQuantitySelector";
import usePreferredSections from "hooks/usePreferredSections";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import EventNotices from "./components/EventNotices";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";

export type SectionDetailsOrBestBlockDetailsArray =
  | SectionDetails[]
  | BestTicketBlockDetails[];

// Calculates the remaining vouchers left for every voucher plan
function remainingVouchers(matchedSubs: SubscriptionDetails[]) {
  const remainingVouchers = matchedSubs.reduce(
    (sum, sub) => sum + sub.availableVouchers,
    0
  );
  return remainingVouchers;
}

// Calculates the remaining reservations slots left for every reservation plan
function availableSlots(matchedSubs: SubscriptionDetails[]) {
  let subscriptionsMaxReservationQuantity = 0;
  let subscriptionsTotalTickets = 0;
  matchedSubs.forEach((sub) => {
    const primaryComponent = sub.planPackage.components.find(
      (component) => component.primary
    );
    const subReservationMaxQuantity = primaryComponent!.quantity;
    const subCurrentTickets = sub.currentTickets;
    subscriptionsMaxReservationQuantity += subReservationMaxQuantity;
    subscriptionsTotalTickets += subCurrentTickets;
  });
  return subscriptionsMaxReservationQuantity - subscriptionsTotalTickets;
}

const StandardSeatSelectionFlow = () => {
  const navigation =
    useNavigation<
      NativeStackNavigationProp<ScheduleStackParamList, "SeatSelection">
    >();
  const dispatch = useAppDispatch();
  const {
    selectedEvent,
    selectedPartySize,
    selectedSubscription,
    isChangingSeats,
    originalTickets,
    originalEventId,
  } = useAppSelector((state) => state.reservationFlow);
  const { tenantSubdomain, tenantType, tenantThemeColor } = useAppSelector(
    (state) => state.tenant
  );

  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { account, accountEvents: events } = useAccount();

  if (!tenantConfig || !account) {
    return null;
  }
  const { ticketProvider } = tenantConfig;

  const usingStaticImage = useMemo(() => {
    const usingStaticSeatMapSubdomains: TenantSubdomain[] = [];
    return usingStaticSeatMapSubdomains.includes(tenantSubdomain!);
  }, [tenantSubdomain]);

  const {
    preferredSections,
    setPreferredSections,
    handleAddPreferredSection,
    handleRemovePreferredSection,
  } = usePreferredSections();

  const [state, setState] = useState<SeatSelectionState>({
    originalPartySize: 0,
    // 0 based index indicates how many seat(s) will be holding, also v-modeled by v-chip-group.
    // fix https://github.com/FanRally/fanrally-core/issues/669
    selection: 0,
    step: 1,
    expansionPanel: false,
    selectedPlanType: "",
    sortBy: PRICE_LOW_TO_HIGH,
    venueMapKey: 1,
    filters: {
      showPremium: true,
      showAccessible: account.seatPreference === "INCLUDE_ACCESSIBLE", // initial value
      sortBy: "Price (Low to High)",
    },
    allBlocks: [],
    mapKey: 1,
  });
  const [listHeight, setListHeight] = useState<number>(0);

  const {
    data: sections,
    isLoading: fetchingSections,
    mutateAsync: fetchSections,
  } = useMutation(fetchSectionsRequest);
  const {
    data: bestBlocks,
    mutateAsync: fetchBestBlocks,
    isLoading: fetchingBlocks,
  } = useMutation(fetchBestBlocksRequest);
  const handleFetchBestBlocks = (partySize: number) => {
    return fetchBestBlocks({
      eventId: selectedEvent?.eventId,
      planPackageId: selectedSubscription?.planPackage.id,
      partySize,
      sections: preferredSections,
      premiumOnly: false,
    });
  };

  const handleFetchSections = (partySize: number) => {
    return fetchSections({
      eventId: selectedEvent?.eventId,
      planPackageId: selectedSubscription?.planPackage.id,
      partySize,
    });
  };

  const { mutateAsync: holdTickets, isLoading: holdingTickets } =
    useMutation(holdTicketsRequest);
  const {
    mutateAsync: holdForUpdateReservation,
    isLoading: holdingUpdateTickets,
  } = useMutation(holdForUpdateReservationRequest);

  const isTicketProviderCaptured = ticketProvider === "CAPTURED";

  const checkIfBlocksContainAccessible = (
    blocks: BestTicketBlockDetails[] | SectionDetails[]
  ) => {
    if (!blocks) return;
    const containAccessible = blocks.some((block) => block.accessible);
    // if (isTicketProviderCaptured) return;
    if (state.filters.showAccessible && !containAccessible) {
      const { supportEmail, supportPhone } = tenantConfig;
      let msg = i18n.t("screens.SeatSelection.preferredSeatNotAvailable", {
        sectionOrGame: selectingBestBlock
          ? i18n.t("screens.SeatSelection.section")
          : i18n.t("screens.SeatSelection.game"),
      });
      if (supportEmail && supportPhone) {
        msg += `call ${supportPhone} or email ${supportEmail}`; // if tenant has both support email and support phone
      } else if (supportEmail) {
        // if tenant has only support email
        msg += `email ${supportEmail}`;
      } else {
        msg += `call ${supportPhone}`;
      }
      toast.warning(msg);
    }
  };

  const filteredSections = useMemo(() => {
    const filteredResult = (sections || [])
      .filter((block) => block.premiumClass === null) // filter out any premium class sections
      .filter((block) => filterAccessible(block, state.filters.showAccessible))
      .filter((block) => filterPremium(block, state.filters.showPremium)) // the show premium flag means if a price is associated with the section
      .filter((block) =>
        filterChangingSeatsSectionRow(block, isChangingSeats, originalTickets)
      )
      .sort((a, b) => sortBlocksFunc(a, b, state.filters.sortBy));

    return filteredResult;
  }, [sections, state.filters]);

  const filteredBlocks = useMemo(() => {
    const sortedBestBlocks = (bestBlocks || [])
      .filter((block) => filterAccessible(block, state.filters.showAccessible))
      .filter((block) => filterPremium(block, state.filters.showPremium))
      .filter((block) =>
        filterChangingSeatsSectionRow(block, isChangingSeats, originalTickets)
      )
      .sort((a, b) => sortBlocksFunc(a, b, state.filters.sortBy));

    if (isTicketProviderCaptured && preferredSections.length > 0) {
      return sortedBestBlocks.filter((block) =>
        preferredSections.includes(block.section)
      );
    }
    return sortedBestBlocks;
  }, [bestBlocks, preferredSections, state.filters]);

  const handleZeroPartySize = (
    selectedPlanType: PlanPackageComponentDetails["type"],
    matchedSubs: SubscriptionDetails[]
  ) => {
    // can't reserve or redeem tickets for this event
    const maximumTicketMsg = i18n.t("screens.SeatSelection.maximumTicketMsg");
    const noReservation = i18n.t("screens.SeatSelection.noReservation");
    const noVoucher = i18n.t("screens.SeatSelection.noVoucher");
    const generateMsg = (msg: string) => {
      return i18n.t("screens.SeatSelection.noTicketMsg", { msg });
    };
    let msg = "";
    switch (selectedPlanType) {
      case "VOUCHER":
        if (remainingVouchers(matchedSubs) === 0) {
          // out of vouchers

          msg = generateMsg(noVoucher);
        } else {
          msg = maximumTicketMsg;
        }
        break;
      case "BANK":
      case "RESERVATION":
        if (availableSlots(matchedSubs) === 0) {
          // out of reservation slots
          msg = generateMsg(noReservation);
        } else {
          msg = maximumTicketMsg;
        }
        break;
    }
    toast.warning(msg);
  };

  const selectingBestBlock = isTicketProviderCaptured || state.step === 2;

  const handleSelectedPartySizeChange = (partySize: number) => {
    dispatch(setSelectedPartySize(partySize));
    setState((state) => ({
      ...state,
      step: 1,
    }));
    setPreferredSections([]);
  };

  const handleBackBtnPress = async () => {
    if (Platform.OS === "web") {
      setState((state) => ({
        ...state,
        step: 1,
        mapKey: state.mapKey + 1,
      }));
    }
    setPreferredSections([]);
  };

  const handleSectionPress = (section: SectionDetails) => {
    setPreferredSections([section.section]);
  };

  const handleBestBlockPress = async (seats: SeatDetails[]) => {
    dispatch(
      setOriginalHeldDetail({
        section: seats[0].section,
        row: seats[0].row,
      })
    );
    if (!selectedSubscription || !selectedEvent) return;

    let response: TicketDetails[] = [];

    if (isChangingSeats) {
      response = await holdForUpdateReservation({
        accountId: account!.id,
        planPackageId: selectedSubscription!.planPackage.id,
        eventId: selectedEvent!.eventId,
        groupCode: originalTickets[0].groupCode!,
        selections: seats,
        premiumOnly: false,
      });
    } else {
      response = await holdTickets({
        planPackageId: selectedSubscription.planPackage.id,
        eventId: selectedEvent.eventId,
        partySize: selectedPartySize,
        selections: seats,
        premiumOnly: false,
      });
    }

    dispatch(setActualHeldDetail(response));
    navigation.push("Reserve");
  };

  const matchedSubs =
    account?.subscriptions.filter(
      (sub) =>
        sub.planPackage.id === selectedSubscription?.planPackage.id &&
        filterIneligibleSubscriptions(sub)
    ) || [];

  const shouldShowGuestPrice =
    selectedSubscription?.planPackage.components.some((comp) =>
      comp.type.includes("GUEST")
    ) || false;

  useEffect(() => {
    async function initSeatData() {
      if (!selectedEvent) return;
      const {
        selectedPlanType,
        partySize,
        defaultSelectedPartySize,
        partySizePlusGuest,
      } = getRemainingSlotsForPlanPackage({
        subscriptions: matchedSubs,
        planPackage: selectedSubscription!.planPackage,
        eventId: selectedEvent!.eventId,
        events,
        isChangingSeats,
        originalEventId,
      });
      // run out of reservation slots for an event, excluding guests
      if (partySize <= 0) {
        handleZeroPartySize(selectedPlanType, matchedSubs);
        return;
      }
      const planPackagePerGameRedemptionMinimum =
        selectedSubscription!.planPackage.perGameRedemptionMinimum;

      // https://github.com/FanRally/fanrally-core/issues/1281 bullet point No.3
      const finalPartySize = isChangingSeats
        ? originalTickets.length
        : defaultSelectedPartySize;

      const maxBetweenPerGameRedemptionMinimumAndPartySize = Math.max(
        finalPartySize,
        planPackagePerGameRedemptionMinimum
      );

      dispatch(
        setSelectedPartySize(
          Math.min(
            maxBetweenPerGameRedemptionMinimumAndPartySize,
            MAX_GUEST_SEATS
          )
        )
      );
      setState((state) => ({
        ...state,
        originalPartySize: Math.min(partySizePlusGuest, MAX_GUEST_SEATS),
        // https://github.com/FanRally/fanrally-core/issues/862
        selectedPlanType,
      }));
    }

    initSeatData();

    // when leaving page, reset reservation flow state
    return () => {
      dispatch(setIsChangingSeat(false));
      dispatch(setOriginalEventId(undefined));
    };
  }, [selectedEvent]);

  useEffect(() => {
    async function fetchAllBlocks() {
      if (selectedPartySize === 0) return;
      let allBlocks: BestTicketBlockDetails[] | SectionDetails[] = [];
      if (isTicketProviderCaptured) {
        allBlocks = await handleFetchBestBlocks(selectedPartySize);
      } else {
        allBlocks = await handleFetchSections(selectedPartySize);
      }
      checkIfBlocksContainAccessible(allBlocks);
      setState((state) => ({
        ...state,
        allBlocks,
      }));
    }
    fetchAllBlocks();
  }, [selectedPartySize]);

  useEffect(() => {
    if (!sections && !bestBlocks) return; // this means page is just started loading

    if (selectingBestBlock) {
      checkIfBlocksContainAccessible(filteredBlocks);
    } else {
      checkIfBlocksContainAccessible(filteredSections);
    }
  }, [state.filters.showAccessible]);

  useEffect(() => {
    async function handlePreferredSectionsChange() {
      if (!isTicketProviderCaptured) {
        if (preferredSections.length === 0) {
          setState((state) => ({ ...state, step: 1 }));
        } else {
          setState((state) => ({ ...state, step: 2 }));
          const blocks = await handleFetchBestBlocks(selectedPartySize);
          checkIfBlocksContainAccessible(blocks);
        }
      }
    }
    handlePreferredSectionsChange();
  }, [preferredSections, state.allBlocks]);

  const onLayout = (event: any) => {
    const { height } = event.nativeEvent.layout;
    setListHeight(height);
  };

  function removePremium(blocks: SectionDetailsOrBestBlockDetailsArray) {
    return blocks.filter((block) => block.premiumClass === null);
  }

  const isLoading =
    fetchingSections ||
    fetchingBlocks ||
    holdingTickets ||
    holdingUpdateTickets;

  return (
    <View className="relative" style={{ height: "100%" }}>
      {selectedEvent && (
        <EventNotices eventGroupNotices={selectedEvent.eventGroupNotices} />
      )}
      <View
        style={{
          flex: 1,
          width: getWidth(),
          alignSelf: "center",
          height: "100%",
        }}
      >
        <View style={{ flex: 0.4, paddingTop: 5 }}>
          <VenueSeatMap
            mapId="seat-selection"
            usingStaticImage={usingStaticImage}
            tenantType={tenantType!}
            tenantSubdomain={tenantSubdomain!}
            theme={tenantThemeColor}
            eventType={selectedEvent!.eventType}
            venueName={selectedEvent!.venueName}
            selectedPartySize={selectedPartySize}
            blocks={
              removePremium(
                state.allBlocks
              ) as SectionDetailsOrBestBlockDetailsArray
            }
            seatGroups={
              selectedSubscription!.planPackage.primaryComponent.seatGroups
            }
            onSectionAdded={handleAddPreferredSection}
            onSectionRemoved={handleRemovePreferredSection}
            venueMapKey={state.mapKey}
            onReset={() => setPreferredSections([])}
            preferredSections={preferredSections}
            includePremium={state.filters.showPremium}
          />
        </View>
        <View
          style={{ flex: 0.08, flexDirection: "row", alignItems: "center" }}
        >
          <SeatQuantitySelector
            size={state.originalPartySize}
            selectedPartySize={selectedPartySize}
            onChange={handleSelectedPartySizeChange}
            minPartySize={
              selectedSubscription?.planPackage.perGameRedemptionMinimum
            }
          />
          <View
            style={{
              paddingHorizontal: 10,
              position: "relative",
            }}
          >
            <Filters
              filters={state.filters}
              onApply={(newFilterValues) =>
                setState((state) => ({ ...state, filters: newFilterValues }))
              }
              bestBlocks={filteredBlocks}
            />
          </View>
        </View>

        <View
          style={{
            flex: 0.5,
            opacity: state.originalPartySize === 0 ? 0.3 : 1,
          }}
          onLayout={onLayout}
        >
          {isLoading ? (
            <InnerLoading />
          ) : (
            <View style={{ paddingHorizontal: 8 }}>
              <SectionList
                sections={filteredSections}
                bestBlocks={filteredBlocks}
                selectingBestBlock={selectingBestBlock}
                height={listHeight}
                handleSectionPress={handleSectionPress}
                handleBestBlockPress={handleBestBlockPress}
                onBackBtnPress={handleBackBtnPress}
                shouldShowGuestPrice={shouldShowGuestPrice}
                ticketProvider={ticketProvider}
                seatSelectionStep={state.step}
                allBlocks={bestBlocks || []}
                filters={state.filters}
              />
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export default StandardSeatSelectionFlow;
